import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ModalDisplayRequest } from '@iupics-components/models/ui-request';
import { ViewType } from '@iupics-components/models/view-type.enum';
import ProcessUiComponent from '@iupics-components/specific/window/process-ui/process-ui.component';
import { FavoriteSelectorComponent } from '@iupics-components/standard/grid/favorite-selector/favorite-selector.component';
import { ViewColumnsService } from '@iupics-components/standard/grid/services/view-columns.service';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { Global } from '@iupics-manager/models/global-var';
import { listenOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import { ToolbarProcess, ToolbarProcessSelectionState } from '@iupics-manager/models/toolbar-item';
import { ApizEventType, ContentProjectionDirective, GridApiService, ToolbarComponent } from '@iupics/apiz-grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { has, isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { KeybindingDirective } from '../../../directives/keybinding.directive';
import UniversalFilterUiComponent from '../universal-filter-ui/universal-filter-ui.component';

@Component({
  selector: 'iu-menu-bar-ui',
  templateUrl: './menu-bar-ui.component.html',
  styleUrls: ['./menu-bar-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ToolbarModule,
    NgStyle,
    NgClass,
    ButtonModule,
    KeybindingDirective,
    TooltipModule,
    UniversalFilterUiComponent,
    TranslateModule,
    NgTemplateOutlet,
    //#region Apiz-grid
    ToolbarComponent,
    ContentProjectionDirective,
    FavoriteSelectorComponent,
    //#endregion
    forwardRef(() => ModalUiComponent),
    ProcessUiComponent,
  ],
})
export default class MenuBarUiComponent implements OnInit, OnDestroy {
  #translate = inject(TranslateService);
  #viewColumnService = inject(ViewColumnsService);

  @Input() isFromForm = false;
  @Input() activeTabID: string;
  @Input() gridView: any;
  /**
   * Sers à cacher le menu-bar detail pour les forms
   */
  @Input() hasUniversalFilter = true;
  @Input() isDeleteable = true;
  @Input() isInsertRecord = true;
  @Input() isReadOnly = false;
  @Input() isSplitView = false;
  @Input() isTabTopLevel = true;
  @Input() tabId: number;
  @Input() visibleButton = true;
  @Input() visibleTreeButton: boolean;

  @Output() readonly changeViewEvent = new EventEmitter();
  @Output() readonly filterChange = new EventEmitter<boolean>();
  @Output() readonly newEmitter = new EventEmitter();
  @Output() readonly openExportDataModalEmitter = new EventEmitter();
  @Output() readonly refreshEmitter = new EventEmitter();
  @Output() readonly removeEmitter = new EventEmitter<MouseEvent>();
  newBtnCondition = false;
  visibleButtonMultiSelection = false;
  tooltipLabels: any;
  isMobile = Global.isMobile();
  showMoreAction = false;

  get isRecordActive(): boolean {
    let response = true;
    if (
      has(this.gridView?.DOMParentComponent?.dataStored?.data, 'IsActive') &&
      !isNil(this.gridView.DOMParentComponent.dataStored.data.IsActive)
    ) {
      response = this.gridView.DOMParentComponent.dataStored.data.IsActive === 'Y';
    }

    return response;
  }

  processes = this.#viewColumnService.processes;

  ngOnInit(): void {
    this.tooltipLabels = {
      add: this.#translate.instant('menuBarUi.add'),
      delete: this.#translate.instant('menuBarUi.delete'),
      refresh: this.#translate.instant('menuBarUi.refresh'),
      export: this.#translate.instant('menuBarUi.export'),
      gridView: this.#translate.instant('menuBarUi.gridView'),
      kanbanView: this.#translate.instant('menuBarUi.kanbanView'),
      calendarView: this.#translate.instant('menuBarUi.calendarView'),
      chartView: this.#translate.instant('menuBarUi.chartView'),
      treeView: this.#translate.instant('menuBarUi.treeView'),
      filter: this.#translate.instant('menuBarUi.filter'),
      search: this.#translate.instant('menuBarUi.search'),
    };
    this.newBtnCondition = !this.isReadOnly && this.isInsertRecord && this.isRecordActive;
  }

  ngOnDestroy(): void {
    this.#processCheckSub?.unsubscribe();
    for (const key of Object.keys(this)) {
      if (this[key] && this[key] instanceof EventEmitter) {
        this[key].complete();
        this[key] = undefined;
      }
    }
  }

  onShowMoreAction(event): void {
    event.stopPropagation();
    this.showMoreAction = !this.showMoreAction;
  }

  setVisibleButton(visible: boolean): void {
    this.visibleButton = visible;
  }

  setVisibleButtonMultiSelection(visible: boolean): void {
    this.visibleButtonMultiSelection = visible;
  }

  switchToGridView(): void {
    this.changeViewEvent.emit(ViewType.GRID);
  }

  switchToKanbanView(): void {
    this.changeViewEvent.emit(ViewType.KANBAN);
  }

  switchToCalendarView(): void {
    this.changeViewEvent.emit(ViewType.CALENDAR);
  }

  switchToChartView(): void {
    this.changeViewEvent.emit(ViewType.CHART);
  }

  switchToTreeView(): void {
    this.changeViewEvent.emit(ViewType.TREE);
  }

  updateButtonLists(): void {
    this.newBtnCondition = !this.isReadOnly && this.isInsertRecord && this.isRecordActive;
  }

  /** grid api of the list (none when the bar is used outside a grid view) */
  readonly #gridApi = inject(GridApiService, { optional: true });
  readonly filterCount = computed(() => this.#gridApi?.filters()?.length ?? 0);

  /** remove every filter chip at once */
  clearFilters(): void {
    if (!this.#gridApi || this.filterCount() === 0) return;
    this.#gridApi.updateAppliedItems([], 'filter', true);
    this.#gridApi.dispatchEvent(ApizEventType.appliedItemRemoved, this.#gridApi.appliedItems());
  }

  /** processes menu: opened by click, not by hover */
  processMenuOpen = signal(false);
  #processMenuEl: HTMLElement = null;
  readonly #click = listenOutsideAngular(
    'click',
    () => this.processMenuOpen(),
    (event) => this.onDocumentClick(event)
  );

  /** processes against the selected rows (null: not checked, too many rows) */
  readonly processStates = signal<Record<string, ToolbarProcessSelectionState> | null>(null);
  readonly processChecking = signal(false);
  #processCheckSub: Subscription = null;

  toggleProcessMenu(event: MouseEvent): void {
    this.#processMenuEl = (event.currentTarget as HTMLElement)?.closest('ul.hover-buttons-list');
    this.processMenuOpen.update((open) => !open);
    if (this.processMenuOpen()) {
      this.#checkProcesses();
    }
  }

  /** same rules as in the edit view, on every selected row (the list row does not hold the needed columns) */
  #checkProcesses(): void {
    this.#processCheckSub?.unsubscribe();
    this.processStates.set(null);
    const check = this.gridView?.checkProcessesForSelection?.(this.processes());
    if (!check) {
      this.processChecking.set(false);
      return;
    }
    this.processChecking.set(true);
    this.#processCheckSub = check.subscribe({
      next: (states: Record<string, ToolbarProcessSelectionState> | null) => {
        this.processStates.set(states);
        this.processChecking.set(false);
      },
      error: () => this.processChecking.set(false),
    });
  }

  isProcessHidden(process: ToolbarProcess): boolean {
    const state = this.processStates()?.[process.componentUUID];
    return !!state && state.total > 0 && state.hidden === state.total;
  }

  allProcessesHidden(): boolean {
    return !!this.processStates() && this.processes().every((p) => this.isProcessHidden(p));
  }

  isProcessBlocked(process: ToolbarProcess): boolean {
    return process.readOnly || this.processChecking() || (this.processStates()?.[process.componentUUID]?.blocked ?? 0) > 0;
  }

  processTitle(process: ToolbarProcess): string {
    if (process.readOnly) return this.#translate.instant('menuBarUi.processReadOnly');
    if (this.processChecking()) return this.#translate.instant('menuBarUi.processChecking');
    const state = this.processStates()?.[process.componentUUID];
    if (state?.blocked > 0) {
      return this.#translate.instant('menuBarUi.processBlocked', { count: state.blocked, total: state.total });
    }
    return process.label;
  }

  closeProcessMenu(focusTrigger = false): void {
    if (!this.processMenuOpen()) return;
    this.processMenuOpen.set(false);
    this.#processCheckSub?.unsubscribe();
    this.processChecking.set(false);
    if (focusTrigger) {
      this.#processMenuEl?.querySelector<HTMLElement>('button.open-icon-title')?.focus();
    }
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.processMenuOpen() && !this.#processMenuEl?.contains(event.target as Node)) {
      this.closeProcessMenu();
    }
  }

  runProcess(process: ToolbarProcess): void {
    // a process that a selected row does not allow cannot run on this selection
    if (!process || this.isProcessBlocked(process)) return;
    this.closeProcessMenu();
    this.showProcessUI(process);
  }

  showProcessUI(process: ToolbarProcess): void {
    this.#viewColumnService.updateModalDisplay.emit(
      ModalDisplayRequest.Process(true, process.processId, { from: 'multiProcess' })
    );
  }
}
